import { Add, DeleteForeverOutlined, Remove } from '@mui/icons-material';
import { Alert, Grid, Stack } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { PartActionTestedCardProps } from './PartActionTestedCard.props';
import { TestLabourTime } from './PartActionTestedCard.styles';
import { CustomPermission, DesiredSerialParentDataResponse, IconColorProps, PartTypeActionLiveSearchItem, PartTypeActionLiveSearchItemCategoryCode } from '../../../../../@types';
import { MerchantDamagePartSerialMenuData } from '../../../../../@types/part.type';
import { Action, PartActionType, PartActionValidateType } from '../../../../../@types/partAction.type';
import { Folders } from '../../../../../@types/uploaded-files.type';
import { Button, ButtonColorProps, ButtonVariantProps } from '../../../../../components/atoms/Button';
import { ImagePopup, ImagePopupSlide } from '../../../../../components/atoms/ImagePopup';
import { LiveSearchBox, LiveSearchListItem } from '../../../../../components/atoms/LiveSearchBox';
import { DropdownItem, SelectMenu } from '../../../../../components/atoms/SelectMenu';
import { TextField, TextFieldTypeProps } from '../../../../../components/atoms/TextField';
import { Typography, TypographyFontWeightProps, TypographyVariantProps } from '../../../../../components/atoms/Typography';
import { PartTypeActionNote } from '../../../../../components/molecules/PartTypeActionNote';
import { PartTypeActionSearch } from '../../../../../components/molecules/PartTypeActionSearch';
import { QUANTITY_CANNOT_EMPTY, TEST_OUTCOMES_RESULTS } from '../../../../../constants/partAction';
import { useGetUploadedImages } from '../../../../../queries/uploadedfiles-query';
import { useGetUserSystemPermissions } from '../../../../../queries/user-query';
import { isEmptyString } from '../../../../../utils/common';
import { blockInteractionAvailable, getBlockInteractionMessage, getFormIsRepeatableStatus, validateInteractionForms } from '../../../../../utils/interactions';
import { partActionConditionValidation, partActionInValidations } from '../../../../../utils/part-action';
import { 
  DefaultCard, 
  StyledCardContent,
  CardTitle,
  CloseWrapper,
  InnerWrapper,
  OuterWrapper
} from '../PartAction.styles';
import { PartTypeActionSearchItem } from '../../../../../components/molecules/PartTypeActionSearchItem';
import { useSearchDesiredParentsForSerial } from '../../../../../queries/live-search-query';
import { adjustTime } from '../../../../../utils/services';
import { FaultCategory, NodeAttributeType } from '../../../../../configs/enums';
import { useGetFilteredNodeAttributesByCustomRange } from '../../../../../queries/structure-query';
import { FormInterceptor } from '../FormInterceptor';
import { Loader } from '../../../../../components/atoms/Loader';
import { COLORS } from '../../../../../configs/colors';

export const PartActionTestedCard: React.FC<PartActionTestedCardProps> = ({
  service,
  value,
  helperText,
  title,
  onChange,
  onClose,
  setHelperText,
  isMobileView,
  interceptedFormsObject,
  setInterceptedFormsObject,
  repeatableFormData,
  setRepeatableFormData,
  setMerchantDamageObjectParcer,
  partActionData,
  isInteractionSubmiting,
  submitInteractionForms
}) => {
  const [isSearchMode, setIsSearchMode] = useState(true);
  const [isSerialized, setIsSerialized] = useState(false);
  const [showSerialSearch, setShowSerialSearch] = useState(false);
  const [partImages, setPartImages] = useState<Array<ImagePopupSlide>>([]);
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [suggestedDesiredSerialParents, setSuggestedDesiredSerialParents] = useState<DesiredSerialParentDataResponse[]>([]);
  const [desiredParentsAvailable, setDesiredParentsAvailable] = useState(false);
  const [faultOptions, setFaultOptions] = useState<Array<DropdownItem>>([]);
  const [locationInteractionSubmitting, setLocationInteractionSubmitting] = useState(false);

  const uploadedImagesQuery = useGetUploadedImages(value.partTypeId || 0, Folders.PartTypesImages);
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const searchDesiredParentsForSerial = useSearchDesiredParentsForSerial();
  const getNodeAttributesQuery = useGetFilteredNodeAttributesByCustomRange(`${service?.contractId || service?.clientId}`, [NodeAttributeType.FaultCategory, NodeAttributeType.FaultCode], service.serviceTypeCode, FaultCategory.Observed, service.actionTypeCode);

  const inputRef: any = useRef(null);

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    setIsSearchMode(isEmptyString(value.name));
    setIsSerialized(value.category ===PartTypeActionLiveSearchItemCategoryCode.Serialised);
    value.partTypeId && uploadedImagesQuery.refetch();
  }, [value.name, value.category, value.partTypeId]);

  useEffect(() => {
    setShowSerialSearch(value.category === PartTypeActionLiveSearchItemCategoryCode.Serialised);
  }, [value.category]);

  useEffect(() => {
    if (uploadedImagesQuery.data) {
      const images: Array<ImagePopupSlide> = uploadedImagesQuery.data.map((obj) => ({
        url: obj.url
      }));
      setPartImages(images);
    }
  }, [uploadedImagesQuery.data]);

  useEffect(() => {
    desiredParentsAvailable && inputRef.current?.focus();
  }, [desiredParentsAvailable]);

  useEffect(() => {
    if (value.testOutcome === 'test-failed') { //TODO: Hardcoded temporarily until an enum is added when test outcomes are added to client/contract config
      getNodeAttributesQuery.refetch();
    }
  }, [value?.testOutcome]);

  const getFaultCodeAndDescripton = (faultCategoryId: number, faultCode: string) => {
    const faultCategory = getNodeAttributesQuery.data?.find((attribute: any) => attribute?.propertyTypeCode === NodeAttributeType.FaultCategory && attribute?.id === faultCategoryId)?.value?.faults?.name || '';
    return `${faultCategory} | ${faultCode}`;
  };

  useEffect(() => {
    if (getNodeAttributesQuery.data) {
      const attributes = getNodeAttributesQuery.data;
      const faultAttributes = attributes.filter((obj: any) => obj.propertyTypeCode === NodeAttributeType.FaultCode);
      const faults: Array<DropdownItem> = faultAttributes.map((obj: any) => {
        const name = obj?.value?.name;
        return {
          label: getFaultCodeAndDescripton(obj?.value?.categoryId, name) || '_',
          value: obj.id
        };
      });
      setFaultOptions(faults);
    }
  }, [getNodeAttributesQuery.data]);

  const handlePartTypeActionSearch = async (liveSearchSelectedItem: PartTypeActionLiveSearchItem) => {
    if (liveSearchSelectedItem?.isPartCreation){
      onChange({
        newPartSerial: liveSearchSelectedItem.name,
        isPartCreation: true,
        action: Action.IN,
        partActionType: PartActionType.TEST,
        outValue: value,
        inValue: value,
        value: value
      });
    } else {
      setHelperText({
        ...helperText,
        in: { 
          ...helperText.in,
          partTypeHelperText: '',
          partSerialHelperText: ''
        },
      });
  
      if (liveSearchSelectedItem?.serial1) {
        onChange({
          outValue: value,
          inValue: value,
          value: {
            ...value,
            id: value.id,
            partTypeId: liveSearchSelectedItem.partTypeId,
            partId: liveSearchSelectedItem.partId,
            name: liveSearchSelectedItem.partTypeName,
            category: liveSearchSelectedItem.categoryCode,
            condition: liveSearchSelectedItem.conditionCode,
            conditionName: liveSearchSelectedItem?.partCondition?.name,
            serial: liveSearchSelectedItem.serial1,
            location: liveSearchSelectedItem.locationName,
            sw: liveSearchSelectedItem.softwareVersion,
            hw: liveSearchSelectedItem.hardwareVersion,
            fw: liveSearchSelectedItem.firmwareVersion,
            ft: liveSearchSelectedItem.fleetTagName,
            serviceId: liveSearchSelectedItem.serviceId,
            isInSiblingHierarchy: liveSearchSelectedItem.isInSiblingHierarchy,
            isAccepted: true
          }  
        });
        const clientContractIds: Array<number> = [service?.clientId || 0, service?.contractId || 0];
        const results = await searchDesiredParentsForSerial.mutateAsync({
          partTypeId: liveSearchSelectedItem.partTypeId,
          clientContractIds: clientContractIds,
          locationId: service.subLocationNodeId || service.mainLocationNodeId
        });
        setSuggestedDesiredSerialParents(results);
        setDesiredParentsAvailable(!!results?.length);
      } else {
        onChange({
          outValue: value,
          inValue: value,
          value: {
            ...value,
            id: value.id,
            partTypeId: liveSearchSelectedItem?.partTypeId,
            ns_partId: liveSearchSelectedItem?.partId,
            name: liveSearchSelectedItem?.partTypeName,
            category: liveSearchSelectedItem?.categoryCode,
            condition: liveSearchSelectedItem?.conditionCode,
            conditionName: liveSearchSelectedItem?.partCondition?.name,
            location: liveSearchSelectedItem?.locationName,
            serial: '',
            sw: liveSearchSelectedItem?.softwareVersion,
            hw: liveSearchSelectedItem?.hardwareVersion,
            fw: liveSearchSelectedItem?.firmwareVersion,
            ft: liveSearchSelectedItem?.fleetTagName,
            isAccepted: true
          }       
        });
      }
    }
  };


  const handlePartTypeSerialSearch = async(val: PartTypeActionLiveSearchItem) => {
    if (val?.isPartCreation) {
      onChange({
        newPartSerial: val.name,
        isPartCreation: true,
        action: Action.IN,
        partActionType: PartActionType.TEST,
        outValue: value,
        inValue: value,
        value: value
      });
    } else {
      setHelperText({
        ...helperText,
        in: {
          ...helperText.in,
          partTypeHelperText: '',
          partSerialHelperText: ''
        },
      });

      onChange({
        outValue: value,
        inValue: value,
        value: {
          ...value,
          partId: val?.partId,
          partTypeId: val?.partTypeId,
          ns_partId: NaN,
          serial: val?.serial1,
          location: val?.locationName,
          condition: val?.conditionCode,
          conditionName: val?.partCondition?.name,
          sw: val?.softwareVersion,
          hw: val?.hardwareVersion,
          fw: val?.firmwareVersion,
          ft: val?.fleetTagName,
          serviceId: val?.serviceId,
          isInSiblingHierarchy: val?.isInSiblingHierarchy
        }
      });
      const clientContractIds: Array<number> = [service?.clientId || 0, service?.contractId || 0];
      const results = await searchDesiredParentsForSerial.mutateAsync({
        partTypeId: val?.partTypeId,
        clientContractIds: clientContractIds,
        locationId: service.subLocationNodeId || service.mainLocationNodeId
      });
      setSuggestedDesiredSerialParents(results);
      setDesiredParentsAvailable(!!results?.length);
    }
  };

  const submitInteractions = async() => {
    if (interceptedFormsObject?.forms && interceptedFormsObject?.forms?.length > 0) {
      const { hasErrorFields, hasByPassedFields, blockInteractionBypassed, updatedForms } = validateInteractionForms(interceptedFormsObject, service, getUserPermissionsQuery.data || []);
      if (updatedForms?.length > 0 && (hasErrorFields || hasByPassedFields || blockInteractionBypassed)) {
        setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
      };
      if (hasErrorFields) {
        return;
      } else {
        const updatedFormsObject = { ...interceptedFormsObject, forms: updatedForms };
        setLocationInteractionSubmitting(true);
        await submitInteractionForms(updatedFormsObject);
        setLocationInteractionSubmitting(false);
      }
    }
  };

  return (
    <DefaultCard>
      <StyledCardContent isMobileView={isMobileView}>
        <CardTitle>{title}</CardTitle>
        {partActionData?.executionCompleted ?
          <Stack justifyContent="center" alignItems="center">
            <Typography variant={TypographyVariantProps.Subtitle1} textColor={COLORS.Gray}>
              Part Action Submitted
            </Typography>
          </Stack>
          : !interceptedFormsObject?.forms || !interceptedFormsObject?.forms?.length ?
            <OuterWrapper>
              <CloseWrapper onClick={onClose} isMobileView={isMobileView}>
                <DeleteForeverOutlined color={IconColorProps.Error} fontSize={isMobileView ? 'large' : 'medium'} />
              </CloseWrapper>
              <InnerWrapper isMobileView={isMobileView} spacing={1}>
                <Stack direction="row" justifyContent="space-between" spacing={1} width="100%" marginTop={1}>
                  <Stack direction="column" spacing={2} width="100%">
                    <PartTypeActionSearch
                      title={showSerialSearch ? 'Part Type Search' : 'Part/Part Type Input'}
                      serviceData={service}
                      locationId={service.subLocationNodeId || service.mainLocationNodeId}
                      value={value?.name || ''}
                      partActionType={PartActionType.TEST}
                      error={!!helperText.in.partTypeHelperText}
                      helperText={helperText.in.partTypeHelperText}
                      onChange={handlePartTypeActionSearch}
                      searchType={showSerialSearch ? 'part-type' : 'part-and-part-type'}
                      IsPartCreationEnabled={showSerialSearch ? false : true}
                    />
                    {showSerialSearch &&
                <PartTypeActionSearch
                  title="Part Serial Search"
                  serialiseType={value.category}
                  locationId={service.subLocationNodeId || service.mainLocationNodeId}
                  partTypeId={`${value.partTypeId}`}
                  serviceData={service}
                  value={value.serial || ''}
                  partActionType={PartActionType.TEST}
                  error={!!helperText.in.partSerialHelperText}
                  helperText={helperText.in.partSerialHelperText}
                  onChange={handlePartTypeSerialSearch}
                  searchType="part"
                />
                    }
                  </Stack>
                  {isSearchMode || <ImagePopup title="Photos" images={partImages} />}
                </Stack>
                {isSearchMode || 
            <>
              {!isSerialized &&
              <Grid>
                <TextField 
                  label="Qty"
                  type={TextFieldTypeProps.Number}
                  value={value.qty}
                  inputProps={{ type: 'number', min: 0, inputMode: 'numeric', pattern: '[0-9]*' }}
                  onChange={(val) => onChange({  
                    outValue: value,
                    value: {
                      ...value,
                      qty: Number(val)
                    },
                    inValue: value 
                  }, true)}
                />
                {!!helperText.in.qtyHelperText && 
                  <Grid size={{ xs: 12 }} mt={1}>
                    <Alert severity="error">{QUANTITY_CANNOT_EMPTY}</Alert>
                  </Grid>
                }
              </Grid>
              }
              {desiredParentsAvailable || value?.desiredParentSerial ?
                <LiveSearchBox
                  inputRef={inputRef}
                  title={'Select Desired Parent'}
                  timeOffset={400} 
                  value={value.desiredParentSerial || ''}
                  feature="part-action"
                  defaultSuggetions={suggestedDesiredSerialParents}
                  disableDelayedInput
                  onChange={(serialPart) => onChange({
                    outValue: value,
                    inValue: value,
                    value:{
                      ...value,
                      desiredParentSerial: serialPart.serial1,
                      desiredParentId: serialPart.partId,
                      desiredParentLocationId: serialPart.locationId,
                      locationTypeCode: 'part'
                    }
                  })}
                  onKeyDown={async(event) => {
                    suggestedDesiredSerialParents.length && setSuggestedDesiredSerialParents([]);
                    if (event.key === 'Enter') {
                      const clientContractIds: Array<number> = [service?.clientId || 0, service?.contractId || 0];
                      const results = await searchDesiredParentsForSerial.mutateAsync({
                        serial: (event.target as HTMLInputElement).value,
                        partTypeId: value.partTypeId,
                        clientContractIds: clientContractIds,
                        locationId: service.subLocationNodeId || service.mainLocationNodeId
                      });
                      results?.length && onChange({
                        outValue: value,
                        inValue: value,
                        value:{
                          ...value,
                          desiredParentSerial: results[0]?.serial1,
                          desiredParentId: results[0]?.partId,
                          desiredParentLocationId: results[0]?.locationId,
                          locationTypeCode: 'part'
                        }
                      });
                    }
                  }}
                  onClearValue={async() => {
                    setDesiredParentsAvailable(true);
                    onChange({
                      outValue: value,
                      inValue: value,
                      value:{
                        ...value,
                        desiredParentSerial: undefined,
                        desiredParentId: undefined,
                        desiredParentLocationId: undefined,
                        locationTypeCode: undefined
                      }
                    });
                    const clientContractIds: Array<number> = [service?.clientId || 0, service?.contractId || 0];
                    const results = await searchDesiredParentsForSerial.mutateAsync({
                      partTypeId: value.partTypeId,
                      clientContractIds: clientContractIds,
                      locationId: service.subLocationNodeId || service.mainLocationNodeId
                    });
                    setSuggestedDesiredSerialParents(results);
                  }}
                  renderItem={(props: any, option: PartTypeActionLiveSearchItem) => { 
                    return (
                      <LiveSearchListItem {...props}>
                        <PartTypeActionSearchItem 
                          name={option.partTypeName}
                          serial={option.serial1}
                          partTypeId={option.partTypeId}
                          partId={option?.partId?.toString() || ''}
                          nsPartId={option?.ns_partId?.toString() || ''}
                          category={option.categoryCode}
                        />
                      </LiveSearchListItem>
                    ); }}
                  onApiInvoke={async (searchValue: string) => {
                    const clientContractIds: Array<number> = [service?.clientId || 0, service?.contractId || 0];
                    const results = await searchDesiredParentsForSerial.mutateAsync({
                      serial: searchValue,
                      partTypeId: value.partTypeId,
                      clientContractIds: clientContractIds,
                      locationId: service.subLocationNodeId || service.mainLocationNodeId
                    });
                    return results;
                  }}
                />
                :
                null
              }
              <Grid size={{ xs: 12 }}>
                <Grid size={{ xs: 12 }} marginBottom={1}>
                  <Typography
                    variant={TypographyVariantProps.Body2}
                    fontWeight={TypographyFontWeightProps.Bold}
                  >
                    Test Outcome / Results
                  </Typography>
                </Grid>
                <SelectMenu
                  label="Test Outcome / Results"
                  id="test-outcomes-results-select"
                  required
                  selectedValue={value?.testOutcome}
                  onChange={(testOutcome) => onChange({
                    outValue: value,
                    inValue: value,
                    value:{
                      ...value,
                      testOutcome: testOutcome,
                      observedFaultId: undefined,
                      observedFaultName: undefined
                    }       
                  })}
                  items={TEST_OUTCOMES_RESULTS} 
                />
              </Grid>
              {value?.testOutcome === 'test-failed' && //TODO: Hardcoded temporarily until an enum is added when test outcomes are added to client/contract config
                <Grid container spacing={1} marginTop={1} marginBottom={1}>
                  <Grid size={{ xs: 12 }}>
                    <Typography
                      variant={TypographyVariantProps.Body2}
                      fontWeight={TypographyFontWeightProps.Bold}
                    >
                      Fault Selections
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <SelectMenu
                      minWidth="300px"
                      id="fault-selections-dropdown"
                      label="Observed Fault"
                      items={faultOptions}
                      selectedValue={value?.observedFaultId || ''}
                      onChange={(faultId) => onChange({
                        outValue: value,
                        inValue: value,
                        value:{
                          ...value,
                          observedFaultId: faultId,
                          observedFaultName: faultOptions?.find((fault) => fault.value === faultId)?.label
                        }       
                      })}
                    />
                  </Grid>
                </Grid>
              }
              <Grid size={{ xs: 12 }}>
                <Stack direction="row" >
                  <TestLabourTime 
                    variant={TypographyVariantProps.Body2} 
                    fontWeight={TypographyFontWeightProps.Bold}
                    className="test-labour-time"
                  >
                    Labour Time:
                  </TestLabourTime>
                  <Button variant={ButtonVariantProps.Icon} onClick={() => onChange({
                    outValue: value,
                    inValue: value,
                    value:{
                      ...value,
                      labourTime: adjustTime(value.labourTime || '00:00', -15)
                    }       
                  })}>
                    <Remove />
                  </Button>
                  <TextField
                    fullWidth={false}
                    value={value.labourTime || '00:00'}
                    helperText={''}
                    onChange={() => null}
                    placeholder="hh:mm"
                  />
                  <Button variant={ButtonVariantProps.Icon} onClick={() => onChange({
                    outValue: value,
                    inValue: value,
                    value:{
                      ...value,
                      labourTime: adjustTime(value.labourTime || '00:00', 15)
                    }       
                  })}>
                    <Add />
                  </Button>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <PartTypeActionNote
                  value={value?.notes || ''}
                  onChange={(note) => onChange({
                    outValue: value,
                    inValue: value,
                    value:{
                      ...value,
                      notes: note
                    }       
                  }, true)}
                />
              </Grid>
              {value.condition && value.serial && partActionConditionValidation(PartActionValidateType.INSTALL, value.condition, value.serial) &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{partActionConditionValidation(PartActionValidateType.INSTALL, value.condition, value.serial)}</Alert>
                </Grid>
              }
              {value.serviceId && service.id !== value.serviceId &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">Warning: This part is marked as Reseved for Service ID {value.serviceId}.</Alert>
                </Grid>
              }
              {value.isInSiblingHierarchy === false && value.serial && partActionInValidations(value?.condition || '', value.serial || '', value?.location || '') &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{partActionInValidations(value?.condition || '', value.serial || '', value?.location || '')}</Alert>
                </Grid>
              }
            </>
                }
              </InnerWrapper>
            </OuterWrapper>
            :
            <Grid>
              {interceptedFormsObject?.forms?.map((form, index) =>
                <FormInterceptor
                  key={index}
                  service={service}
                  value={form}
                  onChange={(editedForms) => {
                    if (repeatableFormData.templateId === 0 && repeatableFormData.templateTitle === '') {
                      const updatedForms = interceptedFormsObject?.forms.map((formData) => formData.id === editedForms.id ? editedForms : formData);
                      setInterceptedFormsObject({
                        ...interceptedFormsObject,
                        forms: updatedForms
                      });
                    }
                  }}
                  onDamagedPartSelect={(Objectkey: string, objectValue: FormData | number | MerchantDamagePartSerialMenuData) => {
                    setMerchantDamageObjectParcer(prevState => ({ ...prevState, [Objectkey]: objectValue }));
                  }}
                  partActionData={partActionData}
                  onAddForm={(templateId, templateTitle) => setRepeatableFormData({ templateId, templateTitle })}
                  isRepeatable={getFormIsRepeatableStatus(form, interceptedFormsObject.forms)}
                  forms={interceptedFormsObject.forms}
                  isMobileView={isMobileView}
                />
              )}
              <br/>
              <Grid>
                <Stack direction="row" spacing={2} alignItems="center" ml={2}>
                  <Button
                    variant={ButtonVariantProps.Primary}
                    color={ButtonColorProps.Success}
                    onClick={submitInteractions}
                    disabled={(isInteractionSubmiting && locationInteractionSubmitting) || blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms)}
                    startIcon={isInteractionSubmiting && locationInteractionSubmitting ? <Loader size={20} /> : null}
                  >
                    Submit
                  </Button>
                  {blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms || []) && <Typography textColor={COLORS.Red}>{getBlockInteractionMessage(interceptedFormsObject?.forms || [])}</Typography>}
                </Stack>
              </Grid>
            </Grid>
        }
      </StyledCardContent>
    </DefaultCard>
  );
};